/**
 * @fileoverview Step 9 — Initial Data Setup with parcel import, inspection
 *              accounts, and seed data organized as collapsible sections.
 * @module system-web/pages/tenants/wizard/steps/InitialDataSetupStep
 *
 * Responsibilities:
 * - Display parcel data import, inspection accounts, and seed data as collapsible
 *   sections so the admin can review each workstream independently.
 * - Write section changes back to the wizard's `initialDataSetup` state.
 *
 * Dependencies:
 * - CollapsibleSection, ParcelImportSection.
 * - lucide-react icons.
 *
 * Architecture:
 * - Local-first form subsection; all state is held in the wizard state and
 *   persisted via the parent's onChange.
 *
 * Notes:
 * - All three sections start expanded so the admin sees every option by default.
 */
import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  Database,
  Bell,
  BookOpen,
  FlaskConical,
  MapPin,
} from 'lucide-react';
import type { WizardState, WizardParcelImport } from '../types';
import type { StepError } from '../validateStep';
import CollapsibleSection from '../../../../components/CollapsibleSection';
import ParcelImportSection from './ParcelImportSection';

interface Props {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
  errors: StepError[];
}

const ACCOUNT_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'residential', label: 'Residential' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'wildland', label: 'Wildland' },
];

const HAZARD_LEVEL_ORDER = ['extreme', 'high', 'medium', 'low'] as const;

/**
 * @component InitialDataSetupStep
 *
 * Purpose:
 * - Collect parcel import, inspection account, and seed data selections.
 *
 * Props:
 * - state:  Current wizard state.
 * - onChange: Merge-style callback for wizard state.
 * - errors: Field-level validation errors for this step.
 *
 * Behavior:
 * - Each subsection lives in a collapsible panel that shows a summary badge.
 * - Parcel import data is managed by `ParcelImportSection`.
 *
 * Data Source:
 * - local-first wizard state (Dexie onboarding draft).
 *
 * Constraints:
 * - No direct API calls; no mock data; no hardcoded URLs.
 */
export default function InitialDataSetupStep({ state, onChange }: Props) {
  const setup = state.initialDataSetup;

  const updateSetup = (patch: Partial<WizardState['initialDataSetup']>) => {
    onChange({ initialDataSetup: { ...setup, ...patch } });
  };

  const [openSections, setOpenSections] = useState<Set<string>>(
    new Set(['parcel', 'inspection', 'seed']),
  );

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const parcelImport: WizardParcelImport = setup.parcelImport ?? { path: 'skip' };

  const addAccount = () => {
    updateSetup({
      inspectionAccounts: [
        ...setup.inspectionAccounts,
        { name: '', accountType: 'general', isDefault: false },
      ],
    });
  };

  const removeAccount = (index: number) => {
    const updated = setup.inspectionAccounts.filter((_, i) => i !== index);
    if (updated.length > 0 && !updated.some((a) => a.isDefault)) {
      updated[0].isDefault = true;
    }
    updateSetup({ inspectionAccounts: updated });
  };

  const updateAccount = (index: number, patch: Partial<WizardState['initialDataSetup']['inspectionAccounts'][0]>) => {
    const updated = setup.inspectionAccounts.map((a, i) => (i === index ? { ...a, ...patch } : a));
    updateSetup({ inspectionAccounts: updated });
  };

  const setDefault = (index: number) => {
    const updated = setup.inspectionAccounts.map((a, i) => ({ ...a, isDefault: i === index }));
    updateSetup({ inspectionAccounts: updated });
  };

  // ── Section summary badges ───────────────────────────────────────────────
  const parcelBadge = (() => {
    if (parcelImport.path === 'skip') return 'Skipped';
    if (parcelImport.path === 'manual') {
      return parcelImport.manualFileName ? `Manual · ${parcelImport.manualFileName}` : 'Manual upload';
    }
    const tokens = parcelImport.tokensPerYear ?? 1;
    const fileName = parcelImport.regridFileName ?? 'No match';
    const contacts = parcelImport.createOwnerContacts === true ? ' · Owner contacts' : '';
    return `Regrid · ${fileName} · ${tokens} update${tokens !== 1 ? 's' : ''}/yr${contacts}`;
  })();

  const inspectionBadge = `${setup.inspectionAccounts.length} account${
    setup.inspectionAccounts.length !== 1 ? 's' : ''
  }`;

  const seedParts: string[] = [];
  if (setup.seedViolationCodes) seedParts.push('Violation codes');
  if (setup.seedNotificationTemplates) seedParts.push('Notification templates');
  if (setup.includeSampleData) seedParts.push('Sample data');
  const seedBadge = seedParts.length > 0 ? seedParts.join(', ') : 'No seed data selected';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Initial Data Setup</h2>
        <p className="text-sm text-gray-500 mt-1">
          Configure parcel data, inspection accounts, and seed data that will be created when the department is provisioned.
        </p>
      </div>

      {/* Parcel Data Import */}
      <CollapsibleSection
        id="parcel"
        isOpen={openSections.has('parcel')}
        onToggle={toggleSection}
        icon={<MapPin className="h-4 w-4" />}
        title="Parcel Data Import"
        badge={parcelBadge}
        badgeActive={parcelImport.path !== 'skip'}
      >
        <ParcelImportSection
          state={state}
          value={parcelImport}
          onChange={(patch) =>
            updateSetup({ parcelImport: { ...parcelImport, ...patch } })
          }
        />
      </CollapsibleSection>

      {/* Inspection Accounts */}
      <CollapsibleSection
        id="inspection"
        isOpen={openSections.has('inspection')}
        onToggle={toggleSection}
        icon={<Database className="h-4 w-4" />}
        title="Inspection Accounts"
        badge={inspectionBadge}
        badgeActive={setup.inspectionAccounts.length > 0}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Inspection accounts segment your inspection workload (e.g., by district, type, or team). At least one is required.
            </p>
            <button
              type="button"
              onClick={addAccount}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Add Account
            </button>
          </div>

          <div className="space-y-3">
            {setup.inspectionAccounts.map((account, i) => (
              <div key={i} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={account.name}
                    onChange={(e) => updateAccount(i, { name: e.target.value })}
                    placeholder="Account name (e.g., General Inspection Account)"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white"
                  />
                </div>
                <select
                  value={account.accountType}
                  onChange={(e) => updateAccount(i, { accountType: e.target.value })}
                  className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  {ACCOUNT_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <button
                    type="button"
                    onClick={() => setDefault(i)}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                      account.isDefault
                        ? 'bg-orange-100 text-orange-700 border-orange-300'
                        : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    {account.isDefault ? 'Default' : 'Set Default'}
                  </button>
                {setup.inspectionAccounts.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeAccount(i)}
                    className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                    aria-label="Remove account"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* Seed Data */}
      <CollapsibleSection
        id="seed"
        isOpen={openSections.has('seed')}
        onToggle={toggleSection}
        icon={<BookOpen className="h-4 w-4" />}
        title="Seed Data"
        badge={seedBadge}
        badgeActive={seedParts.length > 0}
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            Select which standard data sets to pre-populate for this department on provisioning.
          </p>

          <div className="space-y-3">
            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={setup.seedViolationCodes}
                onChange={(e) => updateSetup({ seedViolationCodes: e.target.checked })}
                className="mt-0.5 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <div>
                <p className="text-sm font-medium text-gray-900">Violation Codes</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Standard IFC/NFPA violation codes pre-mapped to the department's selected regulatory framework.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={setup.seedNotificationTemplates}
                onChange={(e) => updateSetup({ seedNotificationTemplates: e.target.checked })}
                className="mt-0.5 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">Notification Templates</p>
                  <Bell className="h-3.5 w-3.5 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Default email and SMS templates for inspection reminders, violations, and system alerts.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-3 border border-amber-100 bg-amber-50 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors">
              <input
                type="checkbox"
                checked={setup.includeSampleData}
                onChange={(e) => updateSetup({ includeSampleData: e.target.checked })}
                className="mt-0.5 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
              />
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">Sample Inspection Data</p>
                  <FlaskConical className="h-3.5 w-3.5 text-amber-600" />
                  <span className="text-xs px-1.5 py-0.5 bg-amber-200 text-amber-800 rounded font-medium">Demo only</span>
                </div>
                <p className="text-xs text-amber-700 mt-0.5">
                  Pre-populate with sample locations, inspection series, and contacts for demonstration purposes.
                  Disable for live production departments.
                </p>
              </div>
            </label>
          </div>
        </div>
      </CollapsibleSection>

      {/* Terms of Service */}
      <div className="border-t border-gray-200 pt-6">
        <label className="flex items-start gap-3 p-4 border-2 border-orange-200 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors">
          <input
            type="checkbox"
            checked={state.tosAcknowledged || false}
            onChange={(e) => onChange({ tosAcknowledged: e.target.checked })}
            className="mt-0.5 h-5 w-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
          />
          <div>
            <p className="text-sm font-semibold text-gray-900">I acknowledge the Terms of Service *</p>
            <p className="text-xs text-gray-600 mt-1">
              By checking this box, you confirm that you have read and agree to the{' '}
              <a href="#" className="text-orange-600 hover:text-orange-700 underline">Terms of Service</a>
              {' '}and{' '}
              <a href="#" className="text-orange-600 hover:text-orange-700 underline">Privacy Policy</a>.
              This department will be provisioned according to the selected subscription plan.
            </p>
          </div>
        </label>
      </div>
    </div>
  );
}
